import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown, PenLine, Car, Armchair, BadgePercent, CalendarDays } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { destinations } from '../data/destinations';
import { seasonalHighlights } from '../data/seasonal';
import { DestinationCard } from '../components/DestinationCard';
import { Marquee } from '../components/Marquee';
import { Reveal } from '../components/Reveal';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1787304226654-d57e4953a3ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkcmFtYXRpYyUyMGFzaWFuJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc5MTg3NTA2MXww&ixlib=rb-4.1.0&q=85';
const WHY_IMAGE = 'https://images.unsplash.com/photo-1772305436753-e308844ecda2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBmYW1pbHklMjB0cmF2ZWx8ZW58MHx8fDE3OTE4NzUwNjF8MA&ixlib=rb-4.1.0&q=85';
const CTA_IMAGE = 'https://images.unsplash.com/photo-1773737847436-044ab59a80c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwcml2YXRlJTIwdG91ciUyMGFzaWF8ZW58MHx8fHwxNzkxODc1MDc4fDA&ixlib=rb-4.1.0&q=85';

const bentoSpans = [
  'md:col-span-2 md:row-span-2 min-h-[26rem] md:min-h-0',
  'md:col-span-2 min-h-[18rem] md:min-h-0',
  'min-h-[18rem] md:min-h-0',
  'min-h-[18rem] md:min-h-0',
  'md:col-span-2 min-h-[18rem] md:min-h-0',
  'min-h-[18rem] md:min-h-0',
  'min-h-[18rem] md:min-h-0',
  'md:col-span-2 min-h-[18rem] md:min-h-0',
];

const whyIcons = [PenLine, Car, Armchair, BadgePercent];

const Home = () => {
  const { lang, t } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-navy" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Asia" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-navy-dark/40" />

        <div className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-8 md:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-6 text-[11px] font-medium uppercase tracking-[0.35em] text-gold"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>

          <h1 className="font-heading text-5xl font-medium leading-[1.05] tracking-tight text-ivory sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {[t.hero.line1, t.hero.line2].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? 'italic text-gold-light' : ''}`}
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ivory/80 md:text-lg"
            data-testid="hero-subheadline"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/destinations"
              data-testid="hero-explore-button"
              className="group inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/plan"
              data-testid="hero-plan-button"
              className="group inline-flex items-center justify-center gap-3 border border-ivory/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-8 hidden items-center gap-3 text-ivory/60 md:flex"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      {/* POPULAR DESTINATIONS — bento grid */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="popular-destinations">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.home.popularKicker}</p>
          <h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight text-navy md:text-5xl">
            {t.home.popularTitle}
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/60">{t.home.popularSub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 md:auto-rows-[270px] md:grid-cols-4">
          {destinations.map((d, i) => (
            <Reveal key={d.slug} delay={Math.min(i * 0.06, 0.3)} className={bentoSpans[i]}>
              <DestinationCard destination={d} className="h-full w-full" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="grain relative bg-navy py-24 text-ivory md:py-32" data-testid="why-choose-us">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:grid-cols-2 md:px-8">
          <div>
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.home.whyKicker}</p>
              <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight md:text-5xl">{t.home.whyTitle}</h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ivory/60">{t.home.whySub}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-12 hidden overflow-hidden md:block">
              <img src={WHY_IMAGE} alt="Family travel" loading="lazy" className="h-80 w-full object-cover transition-transform duration-700 hover:scale-105" />
            </Reveal>
          </div>

          <div className="flex flex-col justify-center">
            {t.home.why.map((item, i) => {
              const Icon = whyIcons[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="group flex gap-6 border-b border-ivory/10 py-8 transition-colors duration-300 hover:border-gold/40" data-testid={`why-item-${i}`}>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-heading text-xl md:text-2xl">{item.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-ivory/60">{item.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-beige py-24 md:py-32" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.home.howKicker}</p>
            <h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight text-navy md:text-5xl">{t.home.howTitle}</h2>
          </Reveal>

          <div className="mt-16 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
            {t.home.steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.08} className="bg-beige">
                <div className="group h-full p-8 transition-colors duration-500 hover:bg-navy md:p-10" data-testid={`how-step-${i + 1}`}>
                  <span className="font-heading text-5xl font-light italic text-gold-dark transition-colors duration-500 group-hover:text-gold md:text-6xl">
                    0{i + 1}
                  </span>
                  <h3 className="mt-6 font-heading text-xl text-navy transition-colors duration-500 group-hover:text-ivory">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/60 transition-colors duration-500 group-hover:text-ivory/60">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEASONAL HIGHLIGHTS */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="seasonal-highlights">
        <Reveal>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.seasonal.kicker}</p>
          <h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight text-navy md:text-5xl">{t.seasonal.title}</h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/60">{t.seasonal.sub}</p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {seasonalHighlights.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.08}>
              <Link
                to={`/plan?package=${item.packageId}&destination=${item.destinationSlug}`}
                data-testid={`seasonal-card-${item.id}`}
                className="group block border border-navy/10 bg-white transition-colors duration-500 hover:border-gold"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title[lang]}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 flex items-center gap-2 bg-gold px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {item.season[lang]}
                  </span>
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-2xl text-navy transition-colors duration-300 group-hover:text-gold-dark">{item.title[lang]}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/60">{item.desc[lang]}</p>
                  <span className="mt-5 inline-flex items-center gap-2 border-b border-gold pb-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold-dark">
                    {t.seasonal.cta}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" data-testid="home-cta">
        <img src={CTA_IMAGE} alt="Private boat in Asia" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy-dark/70" />
        <div className="relative mx-auto max-w-7xl px-5 py-28 md:px-8 md:py-40">
          <Reveal>
            <h2 className="max-w-3xl font-heading text-4xl font-medium tracking-tight text-ivory md:text-6xl">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ivory/70">{t.home.ctaSub}</p>
            <Link
              to="/plan"
              data-testid="cta-plan-button"
              className="group mt-10 inline-flex items-center gap-3 bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
            >
              {t.home.ctaButton}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
